/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Archive, RefreshCw, Trophy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('token') || '';
  return { Authorization: `Bearer ${t}` };
}

function fmtPct(v) {
  if (v === null || v === undefined) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${Number(v).toFixed(2)}%`;
}
function fmtUnits(v) {
  if (v === null || v === undefined) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${Number(v).toFixed(2)}u`;
}

function ColorVal({ value, suffix = '%', neutral = false }) {
  if (value === null || value === undefined) return <span className="text-zinc-500">—</span>;
  const cls = neutral
    ? 'text-zinc-300'
    : value > 0
      ? 'text-emerald-300'
      : value < 0
        ? 'text-rose-300'
        : 'text-zinc-300';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={`font-mono ${cls}`}>
      {sign}{Number(value).toFixed(2)}{suffix}
    </span>
  );
}

export default function AdminMarketsInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cutoff, setCutoff] = useState('2026-04-22');
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/markets/insights`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
    } catch (e) {
      toast.error(`Insights: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleReset() {
    if (!cutoff || !/^\d{4}-\d{2}-\d{2}$/.test(cutoff)) {
      toast.error('Fecha de corte inválida (YYYY-MM-DD)');
      return;
    }
    if (!window.confirm(
      `Vas a archivar el rendimiento de mercado anterior a ${cutoff} y reiniciar el análisis. ¿Confirmar?`
    )) return;
    setResetting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/markets/reset-baseline?cutoff=${cutoff}`,
        { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Reset OK · ${d.snapshot.markets_archived} mercados archivados`);
      load();
    } catch (e) {
      toast.error(`Reset: ${e.message}`);
    } finally {
      setResetting(false);
    }
  }

  const insights = data?.insights || [];
  const baseline = data?.baseline_since;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="markets-insights-page">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold">Mercados · Insights</h1>
            <p className="text-zinc-400 text-sm mt-1">
              Comparativa pre / post baseline para detectar nuevos patrones de rendimiento.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={cutoff}
              onChange={(e) => setCutoff(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="w-36 bg-zinc-900 border-white/10"
              data-testid="cutoff-input"
            />
            <Button
              onClick={handleReset}
              disabled={resetting}
              className="bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="reset-baseline-btn"
            >
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Archive className="w-4 h-4 mr-1" />}
              Reset baseline
            </Button>
            <Button
              onClick={load}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <Card className="bg-zinc-900/50 border-white/5 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <Trophy className="w-4 h-4 text-amber-300" />
            <div className="text-sm text-zinc-200">
              Baseline activo:{' '}
              {baseline ? (
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 ml-1">
                  desde {baseline}
                </Badge>
              ) : (
                <span className="text-rose-300">no establecido</span>
              )}
              <span className="text-zinc-500 ml-3">
                {insights.length} mercados con datos pre o post.
              </span>
            </div>
          </CardContent>
        </Card>

        {loading && (
          <div className="text-center text-zinc-400 py-8">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          </div>
        )}

        {!loading && insights.length === 0 && (
          <Card className="bg-zinc-900/50 border-white/5">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-6 h-6 text-amber-300 mx-auto mb-2" />
              <p className="text-zinc-300 text-sm">
                No hay datos de baseline. Pulsa <span className="text-amber-300">Reset baseline</span> para empezar.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && insights.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-white/5">
            <table className="w-full text-sm" data-testid="insights-table">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Mercado</th>
                  <th className="px-3 py-2 text-right" colSpan={3}>Pre (archivo)</th>
                  <th className="px-3 py-2 text-right" colSpan={3}>Post (live)</th>
                  <th className="px-3 py-2 text-right">Δ yield</th>
                </tr>
                <tr className="text-[10px] text-zinc-500 border-t border-white/5">
                  <th />
                  <th className="px-2 py-1 text-right">n</th>
                  <th className="px-2 py-1 text-right">profit</th>
                  <th className="px-2 py-1 text-right">yield</th>
                  <th className="px-2 py-1 text-right">n</th>
                  <th className="px-2 py-1 text-right">profit</th>
                  <th className="px-2 py-1 text-right">yield</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {insights.map((it) => {
                  const pre = it.pre;
                  const post = it.post;
                  const trend = it.delta_yield_pp;
                  const trendCls = trend === null
                    ? ''
                    : trend > 0
                      ? 'bg-emerald-500/5'
                      : trend < 0
                        ? 'bg-rose-500/5'
                        : '';
                  return (
                    <tr key={it.market}
                        className={`border-t border-white/5 hover:bg-white/5 ${trendCls}`}
                        data-testid={`row-${it.market}`}>
                      <td className="px-3 py-2 font-medium">{it.market}</td>
                      {/* PRE */}
                      <td className="px-2 py-2 text-right text-zinc-400 font-mono">{pre?.sample ?? '—'}</td>
                      <td className="px-2 py-2 text-right">
                        {pre ? <ColorVal value={pre.profit_u} suffix="u" /> : <span className="text-zinc-500">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {pre ? <ColorVal value={pre.yield_pct} /> : <span className="text-zinc-500">—</span>}
                      </td>
                      {/* POST */}
                      <td className="px-2 py-2 text-right text-zinc-400 font-mono">{post?.sample ?? '—'}</td>
                      <td className="px-2 py-2 text-right">
                        {post ? <ColorVal value={post.profit_u} suffix="u" /> : <span className="text-zinc-500">—</span>}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {post ? <ColorVal value={post.yield_pct} /> : <span className="text-zinc-500">—</span>}
                      </td>
                      {/* DELTA */}
                      <td className="px-3 py-2 text-right font-bold">
                        {trend === null
                          ? <span className="text-zinc-500">—</span>
                          : <ColorVal value={trend} suffix="pp" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[11px] text-zinc-500 mt-4">
          Pre = picks anteriores al baseline (archivo congelado). Post = picks desde el baseline en adelante.
          Δ yield = post − pre, en puntos porcentuales. Verde = mercado mejorando, rojo = empeorando.
        </p>
      </div>
    </div>
  );
}
